import { gql } from '@apollo/client';
import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import Logo from '@oberoninternal/travelbase-ds/components/figure/LogoText';
import Modal from '@oberoninternal/travelbase-ds/components/layout/Modal';
import ModalHeader from '@oberoninternal/travelbase-ds/components/layout/ModalHeader';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import useSesame from '@oberoninternal/travelbase-ds/hooks/useSesame';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { StringParam } from 'use-query-params';
import { CLASS_DESKMODE_BUTTON } from '../constants/customizationClassNames';
import { DESKMODE_KEY } from '../constants/storage';
import { useDeskModeLazyQuery } from '../generated/graphql';
import useQueryParams from '../hooks/useQueryParams';
import { clearCartId, getCartId, setCartId } from '../utils/cart';
import getStorageWithExpiry from '../utils/getStorageWithExpiry';
import Stack from './Stack';
import withAuth from '../hoc/withAuth';
import { logout } from '../utils/auth';

export const deskModeQuery = gql`
    query DeskMode($id: ID!) {
        cart(id: $id) {
            id
            order {
                isDeskOrder
            }
        }
    }
`;

const DeskMode: FC<React.PropsWithChildren<unknown>> = () => {
    const { onClose, open, onOpen } = useSesame();
    const [{ cartId: cartIdParam }, setParams] = useQueryParams({ cartId: StringParam });
    const [cartId, setCartIdState] = useState<null | undefined | string>(null);
    const intl = useIntl();
    const [deskMode, setDeskMode] = useState(false);
    const storageUtils = useMemo(() => getStorageWithExpiry('local'), []);
    const lsDeskMode = storageUtils?.getItem(DESKMODE_KEY) === 'true';
    const ref = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        setCartIdState(cartIdParam ?? getCartId());
    }, [cartIdParam]);

    const [fetch, { data, called }] = useDeskModeLazyQuery({
        errorPolicy: 'ignore',
    });

    const isDeskOrder = data?.cart?.order.isDeskOrder;

    useEffect(() => {
        if (cartId && !called) {
            fetch({ variables: { id: cartId } });
        }
    }, [called, cartId, fetch, lsDeskMode]);

    useEffect(() => {
        if (!storageUtils) {
            return;
        }

        if (isDeskOrder) {
            if (cartId) {
                setCartId(cartId);
            }
            storageUtils.setItem(DESKMODE_KEY, 'true');
        }
        if (!isDeskOrder) {
            storageUtils.removeItem(DESKMODE_KEY);
        }
        setDeskMode(!!isDeskOrder);
    }, [storageUtils, isDeskOrder, cartId]);

    const onCancelDeskMode = useCallback(() => {
        if (
            window.confirm(
                intl.formatMessage({
                    defaultMessage: 'Weet je zeker dat je de desk modus wilt afsluiten?',
                })
            )
        ) {
            setParams({ cartId: undefined });
            storageUtils?.removeItem(DESKMODE_KEY);
            clearCartId();
            onClose();
            logout();
            setDeskMode(false);
        }
    }, [intl, onClose, setParams, storageUtils]);

    if (!deskMode) {
        return null;
    }

    return (
        <>
            <DeskModeButton className={CLASS_DESKMODE_BUTTON} ref={ref} open={open} onClick={onOpen}>
                <Logo width="14.4rem" />
            </DeskModeButton>
            <Modal variant="small" open={open} onClose={onClose}>
                <Stack spacing={4}>
                    <ModalHeader variant="small">
                        <FormattedMessage defaultMessage="Desk modus" />
                    </ModalHeader>
                    <Body>
                        <FormattedMessage defaultMessage="Je bevindt je nu in de desk modus. In deze modus is het mogelijk om een desk boeking te maken voor een gast" />
                    </Body>
                    <Button variant="outline" onClick={onCancelDeskMode}>
                        <FormattedMessage defaultMessage="Afsluiten" />
                    </Button>
                </Stack>
            </Modal>
        </>
    );
};

export default withAuth(DeskMode, { withLoadingProp: true });

const DeskModeButton = styled.button<{ open: boolean }>`
    position: fixed;
    top: 3.2rem;
    right: 0;
    z-index: ${({ theme }) => theme.zIndices.sticky};
    box-shadow: 0px 1.6rem 2.4rem 0 rgba(59, 118, 160, 0.03), 0 2.4rem 8rem 0 rgba(59, 118, 160, 0.05);
    border-radius: 8px 0 0 8px;
    outline: none;
    padding: 0.8rem;
    background: white;
    --border-style: 1px solid ${({ theme }) => theme.colors.neutral['20']};
    border-left: var(--border-style);
    border-top: var(--border-style);
    border-bottom: var(--border-style);
    transition: 200ms transform ease-in-out;
    transform: translateX(${({ open }) => (!open ? '12.9rem' : 0)});

    :active,
    :focus {
        border-color: ${({ theme }) => theme.colors.neutral['30']};
    }
    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        :hover {
            transform: translateX(0);
        }
    }
`;
